import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/v2/OfferListEventsContext'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { getExperienceOfferPageURL } from 'lib/offerPageURL'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import { CarouselCardLoadingSkeleton } from 'home/components/CarouselCard/CarouselCardLoadingSkeleton'
import useExperience from 'hooks/Experiences/useExperience'
import { mapExperienceToBookingView } from 'lib/experienceUtils'
import CarouselCard from 'home/components/CarouselCard/CarouselCard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import ExperienceLocationCaption from './ExperienceLocationCaption'
import { ExperienceOfferPriceDetails } from './ExperienceOfferPriceDetails'

interface Props {
  id: string;
  index: number;
  loadIfInView?: boolean;
  inViewThreshold?: number;
  inViewVerticalRootMargin?: string;
  analytics?: boolean;
}

function ExperienceOfferCard(props: Props) {
  const { id, index, inViewThreshold, loadIfInView, analytics, inViewVerticalRootMargin } = props
  const onEvent = useContext(OfferListEventsContext)
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })
  const [loadInViewRef, loadInView] = useInView({ initialInView: false, threshold: inViewThreshold, rootMargin: inViewVerticalRootMargin })
  const [experienceRaw, isExperienceFetching] = useExperience(id, !loadIfInView || loadInView)
  const experience = useMemo(() => {
    return experienceRaw ? mapExperienceToBookingView(experienceRaw) : undefined
  }, [experienceRaw])
  const experienceElementRef = useRef<HTMLDivElement>(null)
  // Since one ref is used for both useInView hooks
  // A new ref (tileElementRef) is created which sets the tileRef/rootRefs
  useEffect(() => {
    inViewRef(experienceElementRef.current)
    loadInViewRef(experienceElementRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceElementRef.current])

  useEffect(() => {
    if (analytics && inView && experience && !isExperienceFetching) {
      const params = {
        experience,
        position: index,
        key: EventDataKey.ExperienceImpression,
      }
      onEvent(OfferListEvents.impression, params)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only
  }, [inView, analytics])

  const onClick = useCallback(() => {
    if (analytics && onEvent) {
      const params = {
        experience,
        position: index,
        key: EventDataKey.ExperienceClick,
      }
      onEvent(OfferListEvents.productClick, params)
    }
  }, [index, experience, onEvent, analytics])

  return (
    <div ref={experienceElementRef}>
      {experience && !isExperienceFetching &&
        <CarouselCard
          bookmarkButton={<ExperienceBookmarkButton offer={experience.originalExperience} />}
          description={experience.description}
          image={experience.image!}
          location={<ExperienceLocationCaption location={experience.location} primaryCategory={experience.primaryCategory} />}
          title={experience.title}
          offerPriceDetails={<ExperienceOfferPriceDetails experience={experience} />}
          offerUrl={getExperienceOfferPageURL(experience.originalExperience)}
          productType={experience.type}
          onClick={onClick}
        />}
      {(!experience || isExperienceFetching) && <CarouselCardLoadingSkeleton />}
    </div>
  )
}

export default ExperienceOfferCard
